<template>
  <div :key="refreshNavigationBarKey" class="navbar">
    <!-- Profile Section -->
    <div class="logo-container" @click="navigateTo('/')">
      <img
        v-if="isCollapsed"
        src="../assets/main-logo-icon.png"
        height="319"
        width="319"
        alt="raas-logo"
      />
      <img
        v-else
        src="../assets/main-logo.png"
        height="319"
        width="927"
        alt="raas-logo"
        class="logo"
      />
    </div>
    <div class="profile" @click="goToProfile">
      <ElAvatar
        :src="user.profile_picture"
        :size="isCollapsed ? 40 : 80"
        shape="circle"
        :icon="UserFilled"
      ></ElAvatar>
      <h2 v-if="!isCollapsed" class="profile-name">
        {{ user.first_name }} {{ user.last_name }}
      </h2>
    </div>
    <ElDivider />
    <ElMenu
      :default-active="$route.path"
      background-color="transparent"
      text-color="var(--quinary-color)"
      active-text-color="var(--secondary-color)"
      class="custom-menu"
      router
    >
      <ElMenuItem class="irma" index="/irma">
        <ElIcon><MagicStick /></ElIcon>
        <span v-if="!isCollapsed">Irma</span>
      </ElMenuItem>
      <ElMenuItem class="irma-files" index="/irma/files">
        <ElIcon><Document /></ElIcon>
        <span v-if="!isCollapsed">Bestanden</span>
      </ElMenuItem>
      <ElDivider />
      <ElMenuItem
        v-if="!isSuperAdmin"
        :index="`/organisations/${subOrganisation || organisation?.id}`"
      >
        <ElIcon><OfficeBuilding /></ElIcon>
        <span v-if="!isCollapsed">Mijn Organisatie</span>
      </ElMenuItem>
      <ElMenuItem index="/contexts">
        <ElIcon><Aim /></ElIcon>
        <span v-if="!isCollapsed">Context</span>
      </ElMenuItem>
      <ElMenuItem index="/risks">
        <ElIcon><WarningFilled /></ElIcon>
        <span v-if="!isCollapsed">Risico's</span>
      </ElMenuItem>
      <ElMenuItem index="/measures">
        <ElIcon><Operation /></ElIcon>
        <span v-if="!isCollapsed">Maatregelen</span>
      </ElMenuItem>
      <ElMenuItem index="/analyse">
        <ElIcon><DataAnalysis /></ElIcon>
        <span v-if="!isCollapsed">Analyse</span>
      </ElMenuItem>
      <ElMenuItem index="/assessments">
        <ElIcon><Memo /></ElIcon>
        <span v-if="!isCollapsed">Assessment</span>
      </ElMenuItem>
      <ElDivider v-if="isSuperAdmin || isAdmin">
        {{ isCollapsed ? "" : "Admin" }}
      </ElDivider>
      <ElMenuItem v-if="isSuperAdmin || isAdmin" :index="'/organisations'">
        <ElIcon><OfficeBuilding /></ElIcon>
        <span v-if="!isCollapsed">Organisaties</span>
      </ElMenuItem>
      <ElMenuItem v-if="isSuperAdmin || isAdmin" index="/users">
        <ElIcon><User /></ElIcon>
        <span v-if="!isCollapsed">Gebruikers</span>
      </ElMenuItem>
      <ElMenuItem v-if="isSuperAdmin" index="/branches">
        <ElIcon><Memo /></ElIcon>
        <span v-if="!isCollapsed">Branches</span>
      </ElMenuItem>
      <ElDivider />
      <!-- Collapse Button -->
    </ElMenu>
    <div class="collapse-button" @click="emit('toggle-collapse')">
      <ElIcon>
        <Component :is="isCollapsed ? Expand : Fold" />
      </ElIcon>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  Aim,
  DataAnalysis,
  Expand,
  Fold,
  MagicStick,
  Memo,
  OfficeBuilding,
  Operation,
  User,
  UserFilled,
  WarningFilled,
  Document,
} from "@element-plus/icons-vue";
import { useSelectOrganisation } from "@/stores/selectOrganisation";
import { onMounted } from "vue";
import { useNavigation } from "@/hooks/useNavigation";
import { useSession } from "@/stores/session";
import { useRefreshComponent } from "@/stores/refreshComponent";

const { isSuperAdmin, isAdmin, user, initUser } = useSession();

const organisationStore = useSelectOrganisation();
const subOrganisation = organisationStore.currentOrganisation;
const organisation = organisationStore.currentTopOrganisation;
const { navigateTo } = useNavigation();
const { refreshNavigationBarKey } = useRefreshComponent();

defineProps({
  isCollapsed: Boolean,
});

// Emit for collapse toggling
const emit = defineEmits(["toggle-collapse"]);

const goToProfile = () => {
  navigateTo(`/users/${user.value.id}`);
};

onMounted(async () => {
  await initUser();
});
</script>

<style scoped>
.navbar {
  @apply w-full h-full block relative;
  border-right: 1px solid var(--el-border-color-lighter);
}

.collapse-button {
  @apply text-[color:var(--quinary-color)] text-2xl cursor-pointer absolute right-4 bottom-4;
}

.profile-name {
  @apply text-[color:var(--quinary-color)] text-xl mt-2;
}

.profile {
  @apply flex-col text-center mb-2 mt-2 transition-all duration-300;

  /* new additions to show it’s clickable */
  @apply cursor-pointer hover:bg-[color:var(--quaternary-color)] p-2;
}

.custom-menu {
  @apply border-none;
}

.logo-container {
  @apply flex justify-center bg-white cursor-pointer;
}

.logo {
  @apply w-[174px] h-[60px];
}
</style>
